import {
  ApplicationInviteSentTracePayload,
  ApplicationSubmittedTracePayload,
  ApplicationUpdateRequestTracePayload,
  ConversationTraceDto,
  ConversationTraceKind,
  EscalationAnswerTracePayload,
  FollowUpTracePayload,
  IdentityVerificationTracePayload,
  QualificationTracePayload,
  SchedulingTracePayload,
  StrategyTracePayload,
} from '../models/conversation-trace.interface';
import {
  bookingSteps,
  followUpBody,
  followUpMeta,
  hiddenSteps,
  identityVerificationStep,
  openerStep,
  screeningSteps,
  showingLogisticsCopy,
  showingLogisticsStep,
  type StepCopy,
  stepMeta,
  stepShortLabels,
} from './trace-copy.const';
import { formatTraceChipTime, formatTraceDayTime, humanizeTraceKey, truncateTraceText } from './trace-format.functions';

export type TraceChipTone = 'grey' | 'green' | 'orange' | 'amber' | 'blue' | 'red';

export interface TraceChipPart {
  /** Bold lead-in of one breakdown row ("Credit score · Tallo asked"). */
  label: string;
  /** Regular continuation after an em-dash — the renter's words or the outcome. */
  detail?: string;
}

export interface TraceChip {
  /** The one glance-readable label: at most two verb-first segments joined with " · ". */
  label: string;
  tone: TraceChipTone;
  /** Panel body: what the highest-priority moment was and why it matters. */
  body: string;
  /** Per-trace breakdown rows; empty when the turn had a single moment (the body covers it). */
  parts: TraceChipPart[];
}

const strategyPayload = (trace: ConversationTraceDto): StrategyTracePayload =>
  trace.payload as unknown as StrategyTracePayload;

/** The renter applied through an apply link and the application went out on its own — nobody sent it. */
const isApplyLinkInvite = (trace: ConversationTraceDto): boolean =>
  (trace.payload as unknown as ApplicationInviteSentTracePayload)?.via === 'apply_link';

/** True when Tallo asked the renter for the screening fact — objective pass or a gate-required ask. */
const askedFor = (trace: ConversationTraceDto): boolean => {
  const payload = strategyPayload(trace);
  return payload.pass === 'objective' || payload.asked === true;
};

/** The opener's dedicated chip — the ladder never sees WELCOME_USER, this is its whole rendering. */
function synthesizeOpenerChip(): TraceChip {
  return {
    label: 'First reply',
    tone: 'grey',
    body: 'Tallo opened the conversation — introducing itself and getting the renter engaged.',
    parts: [],
  };
}

// Generic on purpose: never the payload's compared figures (not always computed correctly) and
// never the reason text (it is close to the renter-facing message, so the panel would echo it).
function qualificationBody(payload: QualificationTracePayload): string {
  return payload.qualified
    ? 'Checks passed, so Tallo opened the tour gate for this property.'
    : "The renter didn't clear this property's screening bar. Tallo lets them down gently and leaves the door open — a co-signer or a corrected figure re-runs the check.";
}

function schedulingBody(payload: SchedulingTracePayload, timeZone?: string): string {
  return payload.phase === 'slot_proposed'
    ? `Tallo proposed ${formatTraceDayTime(payload.time, timeZone)}${payload.tourType ? ` (${payload.tourType.toLowerCase()})` : ''} — the earliest opening inside your showing windows. A concrete slot books faster than an open-ended ask.`
    : `Tallo never confirms a tour on its own. The request for ${formatTraceDayTime(payload.time, timeZone)} is in your queue, and the renter got a holding reply so the thread stays warm while it waits.`;
}

/**
 * A step's copy: the leaf-aware chip for the showing attendance-and-access step (when the row
 * recorded a leaf), the copy maps for everything else, a humanized key for unknown steps.
 */
function stepCopy(trace: ConversationTraceDto): StepCopy {
  const payload = strategyPayload(trace);

  if (payload.step === showingLogisticsStep && (payload.leaf || payload.renterConfirmed)) {
    return showingLogisticsCopy(payload);
  }

  const humanized = humanizeTraceKey(payload.step);
  return {
    label: stepMeta[payload.step]?.label ?? humanized,
    short: stepShortLabels[payload.step] ?? humanized.toLowerCase(),
    body: stepMeta[payload.step]?.body ?? `Tallo handled this part with its "${humanized.toLowerCase()}" playbook.`,
  };
}

function strategyBody(trace: ConversationTraceDto): string {
  return stepCopy(trace).body;
}

function buildParts(trace: ConversationTraceDto, timeZone?: string): TraceChipPart[] {
  if (trace.kind === ConversationTraceKind.STRATEGY) {
    const payload = strategyPayload(trace);
    const stepLabel = stepCopy(trace).label;
    // One row per question the strategy sent to the owner — the exact text sitting in their queue.
    const escalationParts: TraceChipPart[] = (payload.escalatedQuestions ?? []).map((question) => ({
      label: `${stepLabel} · sent to you`,
      detail: `“${truncateTraceText(question, 90)}”`,
    }));
    // A fully-escalated row is its questions alone — the renter's words add nothing beside them.
    if (payload.outcome === 'escalated' && escalationParts.length) {
      return escalationParts;
    }
    const label =
      stepLabel + (payload.outcome === 'escalated' ? ' · sent to you' : '') + (askedFor(trace) ? ' · Tallo asked' : '');
    // The verbatim quote is what the strategy answered; an objective-pass row has no renter ask
    // (a gate-required ask keeps it — the quote is the booking ask that tripped the gate).
    const detail = payload.pass === 'objective' ? undefined : `“${truncateTraceText(payload.relatedText, 90)}”`;
    return [{ label, detail }, ...escalationParts];
  }
  if (trace.kind === ConversationTraceKind.QUALIFICATION) {
    const payload = trace.payload as unknown as QualificationTracePayload;
    return [
      {
        label: 'Verdict',
        detail: payload.qualified
          ? "qualified against this property's requirements"
          : "did not meet this property's requirements",
      },
    ];
  }
  if (trace.kind === ConversationTraceKind.SCHEDULING) {
    const payload = trace.payload as unknown as SchedulingTracePayload;
    return [
      {
        label: 'Tour',
        detail:
          payload.phase === 'request_sent'
            ? `request for ${formatTraceDayTime(payload.time, timeZone)} sent to you`
            : `proposed ${formatTraceDayTime(payload.time, timeZone)}`,
      },
    ];
  }
  if (trace.kind === ConversationTraceKind.FOLLOW_UP) {
    const payload = trace.payload as unknown as FollowUpTracePayload;
    return [
      {
        label: 'Follow-up',
        detail: `${payload.followUpType}${payload.channelSwitched ? ' (email → text)' : ''}`,
      },
    ];
  }
  if (trace.kind === ConversationTraceKind.ESCALATION_ANSWER) {
    const payload = trace.payload as unknown as EscalationAnswerTracePayload;
    return [
      {
        label: 'Your answer',
        detail: payload.answer ? `“${truncateTraceText(payload.answer, 90)}”` : undefined,
      },
    ];
  }
  if (trace.kind === ConversationTraceKind.APPLICATION_INVITE_SENT) {
    return [
      isApplyLinkInvite(trace)
        ? { label: 'Applied via your apply link', detail: 'application sent automatically' }
        : { label: 'Application sent' },
    ];
  }
  // Standalone in practice (nothing is said to the renter when the application waits); listed like the submit.
  if (trace.kind === ConversationTraceKind.APPLICATION_REQUESTED) {
    return [{ label: 'Application requested', detail: 'through your apply link, waiting for you to send it' }];
  }
  if (trace.kind === ConversationTraceKind.APPLICATION_UPDATE_REQUESTED) {
    const payload = trace.payload as unknown as ApplicationUpdateRequestTracePayload;
    return [
      {
        label: 'More info requested',
        detail: payload.message ? `“${truncateTraceText(payload.message, 90)}”` : undefined,
      },
    ];
  }
  // Always standalone in practice (nothing is said to the renter on submit); listed so a row that
  // ever lands on a message still reads as a known kind instead of a humanized fallback.
  if (trace.kind === ConversationTraceKind.APPLICATION_SUBMITTED) {
    const payload = trace.payload as unknown as ApplicationSubmittedTracePayload;
    return [{ label: payload?.resubmitted ? 'Application submitted again' : 'Application submitted' }];
  }
  if (trace.kind === ConversationTraceKind.IDENTITY_VERIFICATION) {
    const payload = trace.payload as unknown as IdentityVerificationTracePayload;
    const detailByPhase: Record<string, string> = {
      link_sent: 'verification link sent to the renter',
      verified: 'government ID + selfie check passed',
      declined: 'identity check did not pass',
    };
    return [
      {
        label: 'ID verification',
        detail: detailByPhase[payload.phase] ?? humanizeTraceKey(String(payload.phase)).toLowerCase(),
      },
    ];
  }
  return [{ label: humanizeTraceKey(trace.kind) }];
}

/**
 * Folds all traces of one Tallo message into ONE glance-readable chip, or null when the turn has
 * nothing chip-worthy (fully silent turns). The label carries at most two segments picked by
 * priority — qualification > tour-approval-needed > application-invite > application-requested >
 * application-update-request > follow-up >
 * identity-verification > escalated-to-you > your-answer-relayed > proposing-a-slot > booking > screening facts >
 * everything else — and the panel itemizes
 * the rest. An opener-only turn gets the dedicated "First reply" chip instead of the ladder.
 * `timeZone` is the property's IANA zone: tour times render there, matching the message text.
 */
export function synthesizeTraceChip(traces: ConversationTraceDto[], timeZone?: string): TraceChip | null {
  const visible = traces.filter(
    (trace) =>
      !(
        trace.kind === ConversationTraceKind.STRATEGY &&
        (hiddenSteps.includes(strategyPayload(trace).step) || strategyPayload(trace).outcome === 'no_answer')
      ),
  );
  if (!visible.length) {
    const opener = traces.find(
      (trace) => trace.kind === ConversationTraceKind.STRATEGY && strategyPayload(trace).step === openerStep,
    );
    return opener ? synthesizeOpenerChip() : null;
  }

  const qualification = visible.find((trace) => trace.kind === ConversationTraceKind.QUALIFICATION);
  const requestSent = visible.find(
    (trace) =>
      trace.kind === ConversationTraceKind.SCHEDULING &&
      (trace.payload as unknown as SchedulingTracePayload).phase === 'request_sent',
  );
  const slotProposed = visible.find(
    (trace) =>
      trace.kind === ConversationTraceKind.SCHEDULING &&
      (trace.payload as unknown as SchedulingTracePayload).phase === 'slot_proposed',
  );
  const applicationInviteSent = visible.find((trace) => trace.kind === ConversationTraceKind.APPLICATION_INVITE_SENT);
  const applicationRequested = visible.find((trace) => trace.kind === ConversationTraceKind.APPLICATION_REQUESTED);
  const applicationUpdateRequested = visible.find(
    (trace) => trace.kind === ConversationTraceKind.APPLICATION_UPDATE_REQUESTED,
  );
  const followUp = visible.find((trace) => trace.kind === ConversationTraceKind.FOLLOW_UP);
  const escalationAnswer = visible.find((trace) => trace.kind === ConversationTraceKind.ESCALATION_ANSWER);
  const identityVerification = visible.find((trace) => trace.kind === ConversationTraceKind.IDENTITY_VERIFICATION);
  const strategies = visible.filter((trace) => trace.kind === ConversationTraceKind.STRATEGY);
  // Escalated = the whole step (outcome) OR part of it (escalatedQuestions on an answered row);
  // a partial row lands in both buckets, so the chip can read "Escalated to you · answering a question".
  const escalated = strategies.filter(
    (trace) => strategyPayload(trace).outcome === 'escalated' || strategyPayload(trace).escalatedQuestions?.length,
  );
  const answered = strategies.filter((trace) => strategyPayload(trace).outcome !== 'escalated');
  const collectedFacts = answered.filter(
    (trace) => screeningSteps.includes(strategyPayload(trace).step) && !askedFor(trace),
  );
  const askedFacts = answered.filter(
    (trace) => screeningSteps.includes(strategyPayload(trace).step) && askedFor(trace),
  );
  const booking = answered.filter((trace) => bookingSteps.includes(strategyPayload(trace).step));
  // The IDV step gets its own segment below rather than the grey generic-step one: plenty of
  // strategies re-send the verification link, so a thread can carry several "ID check requested"
  // turns, and the one the step itself drove has to read differently from them.
  const identityVerificationHandling = answered.find(
    (trace) => strategyPayload(trace).step === identityVerificationStep,
  );
  const otherSteps = answered.filter(
    (trace) =>
      !screeningSteps.includes(strategyPayload(trace).step) &&
      !bookingSteps.includes(strategyPayload(trace).step) &&
      strategyPayload(trace).step !== identityVerificationStep,
  );
  const knownKinds: string[] = [
    ConversationTraceKind.STRATEGY,
    ConversationTraceKind.QUALIFICATION,
    ConversationTraceKind.SCHEDULING,
    ConversationTraceKind.FOLLOW_UP,
    ConversationTraceKind.ESCALATION_ANSWER,
    ConversationTraceKind.IDENTITY_VERIFICATION,
    ConversationTraceKind.APPLICATION_INVITE_SENT,
    ConversationTraceKind.APPLICATION_UPDATE_REQUESTED,
    ConversationTraceKind.APPLICATION_SUBMITTED,
    ConversationTraceKind.APPLICATION_REQUESTED,
  ];
  const unknownKinds = visible.filter((trace) => !knownKinds.includes(trace.kind));

  const segments: string[] = [];
  let tone: TraceChipTone | null = null;
  let headlineBody: string | null = null;
  const push = (segment: string | undefined, segmentTone: TraceChipTone, body?: string): void => {
    if (!segment || segments.length >= 2) {
      return;
    }
    segments.push(segment);
    // The first (highest-priority) segment decides the chip's tone and the panel's headline body.
    if (!tone) {
      tone = segmentTone;
      headlineBody = body ?? null;
    }
  };

  if (qualification) {
    const payload = qualification.payload as unknown as QualificationTracePayload;
    if (payload.qualified) {
      push('qualified ✓', 'green', qualificationBody(payload));
    } else {
      // No figure segment ("credit 540 vs 620 min") — the compared numbers are not always computed
      // correctly, and a wrong number in red is worse than none. The panel body carries the reason.
      push('not qualified', 'red', qualificationBody(payload));
    }
  }
  if (requestSent) {
    push(
      'tour approval needed',
      'amber',
      schedulingBody(requestSent.payload as unknown as SchedulingTracePayload, timeZone),
    );
  }
  // In practice this is the only trace on its message, so the high placement is defensive rather
  // than load-bearing — it just must not lose to a screening fact if the two ever share a turn.
  if (applicationInviteSent) {
    if (isApplyLinkInvite(applicationInviteSent)) {
      push(
        'applied via your link',
        'green',
        'The renter applied through your apply link, so the application went out on its own. This message welcomes them and links to their application.',
      );
    } else {
      push(
        'inviting to apply',
        'green',
        'You invited this renter to apply. The application link itself goes out by email — this message just tells them to expect it.',
      );
    }
  }
  if (applicationRequested) {
    push(
      'application requested',
      'amber',
      "The renter asked to apply through your apply link, and the application did not go out on its own. It waits for you to send it from the renter's profile.",
    );
  }
  // Same defensive placement as the invite: an owner action on the application must not lose to a
  // screening fact if the two ever share a turn.
  if (applicationUpdateRequested) {
    const payload = applicationUpdateRequested.payload as unknown as ApplicationUpdateRequestTracePayload;
    push(
      'asking for more info',
      'amber',
      payload.message
        ? `You asked for more information on the application — “${truncateTraceText(payload.message, 90)}”. Tallo passed it to the renter and re-opened their application so they can add what is missing and submit again.`
        : 'You asked for more information on the application. Tallo passed the request to the renter and re-opened their application so they can add what is missing and submit again.',
    );
  }
  if (followUp) {
    const payload = followUp.payload as unknown as FollowUpTracePayload;
    const meta = followUpMeta[payload.followUpType];
    push(
      meta ? meta.piece(payload) : humanizeTraceKey(payload.followUpType).toLowerCase(),
      'amber',
      meta ? followUpBody(meta, payload) : 'Proactive follow-up sent by Tallo.',
    );
  }
  if (identityVerification || identityVerificationHandling) {
    const payload = identityVerification?.payload as unknown as IdentityVerificationTracePayload | undefined;
    if (payload?.phase === 'verified') {
      push(
        'ID verified ✓',
        'green',
        'The renter passed the government ID + selfie check — the tour can go ahead, and self-guided door access is unblocked.',
      );
    } else if (payload?.phase === 'declined') {
      push(
        'ID verification declined',
        'red',
        "The renter's identity check did not pass, so the tour could not go ahead and no door access was shared.",
      );
    } else if (identityVerificationHandling) {
      // The step re-sends the link as part of its reply, so this turn usually carries a link_sent
      // trace too — "ID check requested · handling ID verification" would say the same thing twice,
      // and "ID check requested" alone would not stand out from the link-only turns around it.
      push('handling ID verification', 'blue', strategyBody(identityVerificationHandling));
    } else {
      push(
        'ID check requested',
        'blue',
        'This property requires ID verification before any tour (government ID + a matching selfie). Tallo sent the renter the link — the tour, and any self-guided door access, waits until they pass.',
      );
    }
  }
  if (escalated.length) {
    push(
      'escalated to you',
      'blue',
      "Tallo didn't have this answer, so it sent the question to you and told the renter it would check. It follows up once you reply.",
    );
  }
  if (escalationAnswer) {
    const payload = escalationAnswer.payload as unknown as EscalationAnswerTracePayload;
    push(
      'relaying your answer',
      'blue',
      payload.question
        ? `You answered “${truncateTraceText(payload.question, 90)}” — Tallo passed it along to the renter in its own words.`
        : 'You answered the escalated question — Tallo passed it along to the renter in its own words.',
    );
  }
  if (slotProposed) {
    const payload = slotProposed.payload as unknown as SchedulingTracePayload;
    push(`proposing ${formatTraceChipTime(payload.time, timeZone)}`, 'orange', schedulingBody(payload, timeZone));
  }
  if (!requestSent && !slotProposed && booking.length) {
    push(stepShortLabels[strategyPayload(booking[0]).step], 'grey', strategyBody(booking[0]));
  }
  if (collectedFacts.length) {
    push(
      `${collectedFacts.map((trace) => stepShortLabels[strategyPayload(trace).step]).join(' + ')} ✓`,
      'grey',
      strategyBody(collectedFacts[0]),
    );
  }
  if (askedFacts.length) {
    push(
      `asking ${askedFacts.map((trace) => stepShortLabels[strategyPayload(trace).step]).join(' + ')}`,
      'grey',
      strategyPayload(askedFacts[0]).pass === 'objective'
        ? 'Tallo raised this itself — it judged this a natural moment to work the screening question into the conversation.'
        : "Tallo needs this fact before the tour gate opens, so it asked — the renter's answer lands on their screening profile.",
    );
  }
  otherSteps.forEach((trace) => {
    const copy = stepCopy(trace);
    push(copy.short, 'grey', copy.body);
  });
  unknownKinds.forEach((trace) => {
    push(humanizeTraceKey(trace.kind).toLowerCase(), 'grey', 'Engine moment recorded by Tallo.');
  });

  const label = segments.length ? segments.join(' · ') : humanizeTraceKey(visible[0].kind);

  const parts = visible.flatMap((trace) => buildParts(trace, timeZone));
  // Escalated questions always itemize — the owner should see the exact text in their queue —
  // otherwise a single-moment turn keeps its panel to the body alone.
  const hasEscalatedQuestions = strategies.some((trace) => strategyPayload(trace).escalatedQuestions?.length);

  return {
    label: label.charAt(0).toUpperCase() + label.slice(1),
    tone: tone ?? 'grey',
    body: headlineBody ?? 'Engine record of this turn.',
    parts: parts.length > 1 || hasEscalatedQuestions ? parts : [],
  };
}
